import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import AuthImg from "../components/AuthImg";
import {
  Save,
  ChevronLeft,
  Wrench,
  Upload,
  X,
} from "lucide-react";

const CATEGORIES = ["Test Equipment", "Hand Tool", "Power Tool", "Safety Gear", "Lifting Gear", "Other"];
const STATUSES = ["Available", "Checked Out", "In Calibration", "Under Repair", "Retired"];
const CONDITIONS = ["Good", "Fair", "Needs Service", "Damaged"];

export default function EquipmentForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const nav = useNavigate();
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [imageUploading, setImageUploading] = useState(false);
  const [inv, setInv] = useState([]);
  const [form, setForm] = useState({
    Tool_ID: "",
    Tool_Name: "",
    Serial_Number: "",
    Category: "Test Equipment",
    Current_Status: "Available",
    Condition: "Good",
    Calibration_Due: "",
    Expected_Return_Date: "",
    Location: "",
    Notes: "",
    Images: [],
    Inventory_Item_Id: "",
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const inventory = await api.get("/inventory", { params: { limit: 1000 } });
        if (!cancelled) setInv(inventory.data || []);
      } catch (e) {
        // Inventory load is non-blocking — the linked-inventory selector just
        // shows an empty list. Log so the failure isn't completely silent.
        console.error("Equipment form: inventory load failed", e);
      }
      if (isEdit) {
        try {
          const { data } = await api.get(`/equipment/${id}`);
          if (cancelled) return;
          setForm({
            Tool_ID: data.Tool_ID || "",
            Tool_Name: data.Tool_Name || "",
            Serial_Number: data.Serial_Number || "",
            Category: data.Category || "Test Equipment",
            Current_Status: data.Current_Status || "Available",
            Condition: data.Condition || "Good",
            Calibration_Due: data.Calibration_Due || "",
            Expected_Return_Date: data.Expected_Return_Date || "",
            Location: data.Location || "",
            Notes: data.Notes || "",
            Images: data.Images || [],
            Inventory_Item_Id: data.Inventory_Item_Id || "",
          });
        } catch (e) {
          setError(formatApiError(e.response?.data?.detail) || "Could not load tool");
        } finally {
          if (!cancelled) setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setImageUploading(true);
    try {
      const newPaths = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/files/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newPaths.push(data.path);
      }
      setForm((f) => ({ ...f, Images: [...f.Images, ...newPaths] }));
      toast.success(`Uploaded ${files.length} image${files.length > 1 ? "s" : ""}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setImageUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (path) =>
    setForm((f) => ({ ...f, Images: f.Images.filter((p) => p !== path) }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.Tool_Name.trim()) return setError("Tool Name is required.");
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/equipment/${id}`, form);
        toast.success("Tool updated");
        nav(`/equipment/${id}`);
      } else {
        const { data } = await api.post("/equipment", form);
        toast.success(`Tool created: ${data.Tool_ID}`);
        nav(`/equipment/${data.id}`);
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-slate-500">Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-32 md:pb-10">
      <Link
        to="/equipment"
        className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-2"
        data-testid="equipment-back"
      >
        <ChevronLeft className="h-4 w-4" /> Back
      </Link>
      <div className="flex items-center gap-2 mb-4">
        <Wrench strokeWidth={2.5} className="h-7 w-7 text-amber-600" />
        <h1 className="font-display text-2xl sm:text-3xl uppercase text-slate-900 tracking-tight">
          {isEdit ? "Edit Tool" : "Add Tool"}
        </h1>
      </div>

      <form onSubmit={onSubmit} className="space-y-4" data-testid="equipment-form">
        <Field label={`Tool ID${isEdit ? "" : " (leave blank to auto-generate)"}`}>
          <input
            value={form.Tool_ID}
            onChange={(e) => setField("Tool_ID", e.target.value.toUpperCase())}
            data-testid="field-tool-id"
            disabled={isEdit}
            placeholder="TOOL-2026-00001"
            className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base font-mono bg-white focus:border-slate-900 outline-none disabled:bg-slate-100"
          />
        </Field>
        <Field label="Tool Name *">
          <input
            required
            value={form.Tool_Name}
            onChange={(e) => setField("Tool_Name", e.target.value)}
            data-testid="field-tool-name"
            placeholder="e.g. Meggar 1000V"
            className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base font-bold bg-white focus:border-slate-900 outline-none"
          />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Serial Number">
            <input
              value={form.Serial_Number}
              onChange={(e) => setField("Serial_Number", e.target.value)}
              data-testid="field-serial"
              className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none"
            />
          </Field>
          <Field label="Category">
            <select
              value={form.Category}
              onChange={(e) => setField("Category", e.target.value)}
              data-testid="field-category"
              className="rg-tap w-full border-2 border-slate-300 rounded-sm px-2 text-base bg-white focus:border-slate-900 outline-none"
            >
              {CATEGORIES.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Current Status">
            <select
              value={form.Current_Status}
              onChange={(e) => setField("Current_Status", e.target.value)}
              data-testid="field-status"
              className="rg-tap w-full border-2 border-slate-300 rounded-sm px-2 text-base bg-white focus:border-slate-900 outline-none"
            >
              {STATUSES.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </Field>
          <Field label="Condition">
            <select
              value={form.Condition}
              onChange={(e) => setField("Condition", e.target.value)}
              data-testid="field-condition"
              className="rg-tap w-full border-2 border-slate-300 rounded-sm px-2 text-base bg-white focus:border-slate-900 outline-none"
            >
              {CONDITIONS.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Calibration Due">
            <input
              type="date"
              value={form.Calibration_Due}
              onChange={(e) => setField("Calibration_Due", e.target.value)}
              data-testid="field-cal-due"
              className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none"
            />
          </Field>
          <Field label="Location">
            <input
              value={form.Location}
              onChange={(e) => setField("Location", e.target.value)}
              data-testid="field-location"
              placeholder="Shelf A · Truck 4 · Substation"
              className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none"
            />
          </Field>
        </div>
        <Field label="Linked Inventory Item (optional)">
          <select
            value={form.Inventory_Item_Id}
            onChange={(e) => setField("Inventory_Item_Id", e.target.value)}
            data-testid="field-inventory-link"
            className="rg-tap w-full border-2 border-slate-300 rounded-sm px-2 text-base bg-white focus:border-slate-900 outline-none"
          >
            <option value="">— None —</option>
            {inv.map((i) => (
              <option key={i.id} value={i.id}>
                {i.Item_Name} ({i.Item_Type})
              </option>
            ))}
          </select>
        </Field>
        <Field label="Notes">
          <textarea
            value={form.Notes}
            onChange={(e) => setField("Notes", e.target.value)}
            data-testid="field-notes"
            rows={3}
            className="w-full border-2 border-slate-300 rounded-sm px-3 py-2 text-base bg-white focus:border-slate-900 outline-none"
          />
        </Field>
        <Field label="Photos">
          <div className="space-y-3">
            <label
              htmlFor="tool-image-upload"
              className="rg-tap w-full bg-white border-2 border-dashed border-slate-400 hover:border-slate-900 rounded-sm flex items-center justify-center gap-2 font-bold uppercase tracking-wide text-slate-700 cursor-pointer min-h-[60px]"
              data-testid="tool-upload-label"
            >
              <Upload strokeWidth={2.5} className="h-5 w-5" />
              {imageUploading ? "Uploading…" : "Add photos"}
            </label>
            <input
              id="tool-image-upload"
              type="file"
              accept="image/*"
              multiple
              capture="environment"
              onChange={onUpload}
              className="hidden"
              data-testid="tool-image-input"
            />
            {form.Images.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="tool-image-thumbs">
                {form.Images.map((p) => (
                  <div key={p} className="relative aspect-square">
                    <AuthImg path={p} className="w-full h-full object-cover border-2 border-slate-300 rounded-sm" />
                    <button
                      type="button"
                      onClick={() => removeImage(p)}
                      data-testid={`remove-tool-image-${p}`}
                      className="absolute -top-2 -right-2 h-7 w-7 bg-rose-700 text-white grid place-items-center rounded-sm border-2 border-white"
                    >
                      <X strokeWidth={3} className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Field>

        {error && (
          <div className="border-2 border-rose-600 bg-rose-50 text-rose-800 text-sm font-bold px-3 py-2 rounded-sm" data-testid="form-error">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={saving || imageUploading}
          data-testid="save-tool-btn"
          className="rg-tap-xl w-full bg-blue-700 hover:bg-blue-800 text-white font-display uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 disabled:opacity-60 min-h-[60px] text-base sm:text-lg border-2 border-blue-900"
        >
          <Save strokeWidth={3} className="h-5 w-5" />
          {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Tool"}
        </button>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-700 mb-1">
        {label}
      </div>
      {children}
    </label>
  );
}
